import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FileText, Plus } from 'lucide-react'

import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { NewPromptDialog } from '@/components/NewPromptDialog'
import { useAuthStore } from '@/stores/auth'
import type { Prompt, PromptListResponse } from '@sdr-agend/admin-schemas'

/**
 * `/admin/prompts` — master listing. Each row represents the active prompt of
 * a campaign; clicking through opens the detail page for that prompt.
 *
 * Story 3.7 §AC #6: list shows name (campaign), current version, activatedAt,
 * lastUpdated. Read access is open to every authenticated role; mutations
 * happen on the detail / history pages and are gated server-side.
 *
 * Story 3.15: "Novo Prompt" button at the top opens a modal (RBAC
 * admin/manager) that posts to `POST /admin/prompts`. Closes the TODO of
 * Story 3.7 (no curl-only path to first prompt).
 */
export function PromptsPage(): React.ReactElement {
  const role = useAuthStore((s) => s.user?.role)
  const canCreate = role === 'admin' || role === 'manager'
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data, isLoading, isError } = useQuery<PromptListResponse>({
    queryKey: ['admin-prompts'],
    queryFn: async () => api<PromptListResponse>('/admin/prompts'),
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Prompts</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie os prompts da AI por campanha. Cada edição cria nova versão; a versão
            ativa é a usada pelo agente em produção.
          </p>
        </div>
        {canCreate && (
          <Button type="button" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Novo Prompt
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Campanha</TableHead>
              <TableHead>Versão ativa</TableHead>
              <TableHead>Ativada em</TableHead>
              <TableHead>Última edição</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-destructive">
                  Erro ao carregar prompts.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && (data?.prompts ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  Nenhum prompt ativo. Use o botão <strong>Novo Prompt</strong> acima.
                </TableCell>
              </TableRow>
            )}
            {(data?.prompts ?? []).map((p: Prompt) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.campaignName}</TableCell>
                <TableCell>
                  <Badge variant="secondary">v{p.version}</Badge>
                </TableCell>
                <TableCell>{new Date(p.createdAt).toLocaleString('pt-BR')}</TableCell>
                <TableCell>{new Date(p.updatedAt).toLocaleString('pt-BR')}</TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="ghost" size="sm">
                    <Link to={`/admin/prompts/${p.id}`} aria-label={`Abrir prompt ${p.campaignName}`}>
                      <FileText className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <NewPromptDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  )
}

export default PromptsPage
